import { type FC, type ReactElement, useRef, useState } from "react";

import { CustomButton, DialogBody } from "@/components/atoms";
import { Dialog, DialogClose, DialogTrigger } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { formatTestId } from "@/utils/formatTestId";

interface BasicModalProps {
  trigger?: ReactElement;
  title: string;
  body: ReactElement;
  action?: () => void;
  btnTitle: string;
  classes?: string;
  isPending?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigerClasses?: string;
  disabled?: boolean;
  footer?: boolean;
}

export const BasicModal: FC<BasicModalProps> = ({
  trigger,
  title,
  body,
  action,
  btnTitle,
  classes,
  isPending = false,
  isOpen: controlledIsOpen,
  onOpenChange,
  trigerClasses,
  disabled,
  footer = true,
}) => {
  const [isOpen, setOpen] = useState(false);
  const actionLockRef = useRef(false);

  const handleAction = () => {
    if (isPending || actionLockRef.current) return;
    actionLockRef.current = true;
    action?.();
    if (!controlledIsOpen) setOpen(false);
    onOpenChange?.(false);
  };

  return (
    <>
      <Dialog
        open={controlledIsOpen ?? isOpen}
        onOpenChange={open => {
          if (disabled && open) return;
          if (open) {
            actionLockRef.current = false;
          }
          if (controlledIsOpen) {
            onOpenChange?.(open);
          } else {
            setOpen(open);
          }
        }}
      >
        {trigger && (
          <DialogTrigger asChild className={trigerClasses}>
            <button
              className={cn("cursor-pointer", classes)}
              data-testid={`$menu-${formatTestId(title)}-button`}
              disabled={disabled}
            >
              {trigger}
            </button>
          </DialogTrigger>
        )}

        <DialogBody title={title} classes="gap-2">
          {body}

          {footer && (
            <div className="flex flex-col gap-5 pt-2">
              <div className="flex justify-between gap-3">
                <DialogClose asChild>
                  <CustomButton
                    variant="light"
                    type="button"
                    className="flex-1"
                    data-testid="assign-cancel-button"
                  >
                    Cancel
                  </CustomButton>
                </DialogClose>

                <CustomButton
                  type="button"
                  className="flex-1"
                  data-testid="assign-submit-button"
                  onClick={handleAction}
                  variant="destructive"
                  isPending={isPending}
                  disabled={isPending}
                >
                  {btnTitle}
                </CustomButton>
              </div>
            </div>
          )}
        </DialogBody>
      </Dialog>
    </>
  );
};
